import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, Trash2, ChevronLeft, ChevronRight, CalendarDays,
} from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InheritanceSourceBadge, type InheritanceSourceKind } from '@/components/InheritanceSourceBadge';
import { TimeRangeInput, type TimeRangeInputValue, computesCrossesMidnight } from '@/components/TimeRangeInput';
import { DOW_LABELS, buildDateList, dayOfWeekOf, dayTextClass, holidayNameOf, resolveDayKind } from '@/lib/calendar-day';
import { useHolidays } from '@/hooks/useHolidays';
import {
  TIME_BAND_COLOR_TOKENS, TIME_BAND_COLOR_CLASSES, TIME_BAND_COLOR_LABELS,
  TIME_BAND_ICON_KEYS, TIME_BAND_ICON_LABELS, TIME_BAND_ICON_COMPONENTS, type TimeBand,
} from '@/lib/time-band';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/settings/shift-patterns` (v0.4 章④ F-3、D-77 / 追加7要件③、D-234 §6-3(b)(c))。
 * ⚠️ シフトパターンの時刻は営業時間から導出しない (D-77、§6-0 原則2)。パターンは名前付きプリセットに過ぎず、
 * 内部表現は常に開始〜終了時刻。両タブとも「対象ノード」を共有し、ノードごとの実効値 + 継承元バッジを表示する。
 * 「営業時間」タブには年間カレンダー (定休日・例外日・祝日の重ね合わせ + 一括登録) を追加している。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-3 / §4-11
 *       docs/design/v04-shift-additional-requirements.md §6-3(b)(c) (年間カレンダー)
 */

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

type Pattern = { pattern_id: string; code: string; label: string; color: string | null; sort_order: number };
type PolicyInfo = { override_mode: string; requires_approval: boolean };
type ResolvedTime = {
  value: { start_time: string; end_time: string; crosses_midnight: boolean; break_minutes: number } | null;
  source_node_id: string | null; source_kind: InheritanceSourceKind; source_node_label: string | null; policy: PolicyInfo;
};
type ResolvedDay = {
  day_of_week: number;
  value: { open_time: string | null; close_time: string | null; crosses_midnight: boolean; is_closed: boolean } | null;
  source_node_id: string | null; source_kind: InheritanceSourceKind; source_node_label: string | null; policy: PolicyInfo;
};
/** 追加7要件④ (D-240): shift_draft_skip_closed_days は汎用の GET /inheritable-settings/:key/resolve
 *  で解決する (scalar 設定、wage_basis と同型)。 */
type ResolvedSkipClosedDays = {
  value: boolean | null;
  source_node_id: string | null; source_kind: InheritanceSourceKind; source_node_label: string | null;
};
/** 追加7要件⑤ (D-235): shift_preference_input_mode も同型の scalar 設定。 */
export type PreferenceInputMode = 'pattern' | 'time_range' | 'both';
type ResolvedPreferenceInputMode = {
  value: PreferenceInputMode | null;
  source_node_id: string | null; source_kind: InheritanceSourceKind; source_node_label: string | null;
};
/** 使い勝手フィードバック (2026-09-04、D-U13): shift_print_display_mode も同型の scalar 設定
 *  (「常にパターン名」/「常に時刻」の2択、フォールバック無し)。 */
export type PrintDisplayMode = 'pattern_name' | 'time';
type ResolvedPrintDisplayMode = {
  value: PrintDisplayMode | null;
  source_node_id: string | null; source_kind: InheritanceSourceKind; source_node_label: string | null;
};
/** 追加7要件⑥ (D-236): shift_past_edit_grace_days / shift_period_max_future_days も同型の scalar
 *  設定 (NULL = 制限なし)。 */
type ResolvedNullableDays = {
  value: number | null;
  source_node_id: string | null; source_kind: InheritanceSourceKind; source_node_label: string | null;
};

const SAVE_SCOPE = { node: 'node', tenant: 'tenant' } as const;
type SaveScope = typeof SAVE_SCOPE[keyof typeof SAVE_SCOPE];

function NodeSelector({ nodeId, onChange }: { nodeId: string; onChange: (id: string) => void }) {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  return (
    <div className="min-w-0 w-full sm:w-72">
      <label className="block text-xs text-text-sub mb-1">対象ノード (実効値の確認元)</label>
      <Select value={nodeId || undefined} onValueChange={onChange}>
        <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="ノードを選ぶ" /></SelectTrigger>
        <SelectContent>
          {flatNodes.map(({ node, depth }) => (
            <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** ⚠️ 既定の文言 (「テナント全体の既定」) は shift_pattern_time/business_hours のような「上書き型」
 *  (node が tenant 既定を上書きする row_set 継承) を前提にしている。祝日 (tenant_holidays) は
 *  additive (テナント全体 ∪ 店舗固有の和集合、useHolidays.ts 参照) のため意味が異なり、
 *  `tenantLabel`/`tenantHelperText` で文言を差し替えられるようにしている。 */
function SaveScopeRadio({
  scope, onChange, tenantLabel = 'テナント全体の既定', tenantHelperText = 'テナント全体の既定を変更するには全店舗の管理権限が必要です。',
}: { scope: SaveScope; onChange: (s: SaveScope) => void; tenantLabel?: string; tenantHelperText?: string }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs text-text-sub">保存範囲</label>
      <div className="flex rounded-full border border-surface-border overflow-hidden w-fit" role="group" aria-label="保存範囲">
        {([[SAVE_SCOPE.node, 'このノードのみ'], [SAVE_SCOPE.tenant, tenantLabel]] as const).map(([v, label]) => (
          <button key={v} type="button" aria-pressed={scope === v} onClick={() => onChange(v)}
            className={`px-3 min-h-[44px] sm:min-h-[32px] text-xs font-medium ${scope === v ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
            {label}
          </button>
        ))}
      </div>
      {scope === SAVE_SCOPE.tenant && <p className="text-xs text-text-sub">{tenantHelperText}</p>}
    </div>
  );
}

function PatternTimeEditor({ patternId, nodeId, canManage, onClose }: { patternId: string; nodeId: string; canManage: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<ResolvedTime>({
    queryKey: ['shift-pattern-times', patternId, nodeId],
    queryFn: () => apiFetch<ResolvedTime>(`/settings/shift-patterns/${patternId}/times?node_id=${encodeURIComponent(nodeId)}`),
  });
  const [scope, setScope] = useState<SaveScope>(SAVE_SCOPE.node);
  const [value, setValue] = useState<TimeRangeInputValue>({ startTime: null, endTime: null });
  const [breakMinutes, setBreakMinutes] = useState('0');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!data || initialized) return;
    setValue({ startTime: data.value?.start_time ?? '09:00', endTime: data.value?.end_time ?? '17:00' });
    setBreakMinutes(String(data.value?.break_minutes ?? 0));
    setScope(data.source_kind === 'tenant_default' || data.source_kind === 'none' ? SAVE_SCOPE.tenant : SAVE_SCOPE.node);
    setInitialized(true);
  }, [data, initialized]);

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/settings/shift-patterns/${patternId}/times`, {
      method: 'PUT',
      body: JSON.stringify({
        node_id: scope === SAVE_SCOPE.tenant ? null : nodeId,
        start_time: value.startTime, end_time: value.endTime,
        crosses_midnight: computesCrossesMidnight(value),
        break_minutes: Number(breakMinutes) || 0,
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shift-pattern-times'] }); toast.success('保存しました'); onClose(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: () => apiFetch(`/settings/shift-patterns/${patternId}/times?node_id=${encodeURIComponent(nodeId)}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shift-pattern-times'] }); toast.success('このノードの上書きを削除しました'); onClose(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const canSave = !!value.startTime && !!value.endTime;

  return (
    <Modal open title="時刻の設定" onClose={onClose} size="sm"
      footer={(
        <>
          {canManage && data?.source_kind === 'self' && (
            <Button variant="ghost" onClick={() => deleteMut.mutate()} loading={deleteMut.isPending}>この上書きを削除</Button>
          )}
          <Button variant="secondary" onClick={onClose}>{canManage ? 'キャンセル' : '閉じる'}</Button>
          {canManage && (
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
          )}
        </>
      )}>
      {isLoading || !initialized ? <div className="text-text-sub">読み込み中…</div> : (
        <div className="space-y-4">
          <InheritanceSourceBadge sourceKind={data!.source_kind} sourceNodeLabel={data!.source_node_label} />
          <TimeRangeInput value={value} onChange={setValue} disabled={!canManage} />
          <div>
            <label className="block text-sm text-text-sub mb-1">休憩時間 (分)</label>
            <input type="number" min={0} value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} className={inputCls} disabled={!canManage} />
          </div>
          {canManage && <SaveScopeRadio scope={scope} onChange={setScope} />}
        </div>
      )}
    </Modal>
  );
}

const PREFERENCE_INPUT_MODE_OPTIONS: { value: PreferenceInputMode; label: string }[] = [
  { value: 'both', label: '両方 (パターン選択・時刻入力のどちらも使える)' },
  { value: 'pattern', label: 'パターン選択のみ' },
  { value: 'time_range', label: '時刻入力のみ' },
];

/** 追加7要件⑤ (D-235): 希望入力モードの切替パネル。DraftSkipClosedDaysPanel と同型
 *  (scalar 設定 + SaveScopeRadio、node_scalar_settings に相乗り)。 */
function PreferenceInputModePanel({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<ResolvedPreferenceInputMode>({
    queryKey: ['shift-preference-input-mode', nodeId],
    queryFn: () => apiFetch(`/inheritable-settings/shift_preference_input_mode/resolve?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });
  const initial = useRef<{ scope: SaveScope; value: PreferenceInputMode } | null>(null);
  const [scope, setScope] = useState<SaveScope>(SAVE_SCOPE.node);
  const [value, setValue] = useState<PreferenceInputMode>('both');
  useEffect(() => {
    if (!data) return;
    // ⚠️ DraftSkipClosedDaysPanel/PatternTimeEditor と同じ安全側の判定 (ancestor は自ノードへの
    // 新規上書きを既定にし、意図せずテナント全体の既定を書き換えることを防ぐ)。
    const seeded = {
      scope: (data.source_kind === 'tenant_default' || data.source_kind === 'none' ? SAVE_SCOPE.tenant : SAVE_SCOPE.node) as SaveScope,
      value: data.value ?? 'both',
    };
    initial.current = seeded;
    setScope(seeded.scope);
    setValue(seeded.value);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => apiFetch('/settings/shift-preferences/input-mode', {
      method: 'PUT',
      body: JSON.stringify({ node_id: scope === SAVE_SCOPE.tenant ? null : nodeId, value }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shift-preference-input-mode'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const dirty = !!data && (!initial.current || value !== initial.current.value || scope !== initial.current.scope);

  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-text-body">シフト希望の入力方法</h3>
        {data && <InheritanceSourceBadge sourceKind={data.source_kind} sourceNodeLabel={data.source_node_label} />}
      </div>
      {isLoading || !data ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : (
        <>
          <Select value={value} onValueChange={(v) => setValue(v as PreferenceInputMode)} disabled={!canManage}>
            <SelectTrigger className="w-full sm:w-80 min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PREFERENCE_INPUT_MODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-text-sub">
            シフト希望・恒常パターン・編成ボードでの入力方法を切り替えます。「パターン選択のみ」はパターンが登録されていない場合は時刻入力にフォールバックします。
          </p>
        </>
      )}
      {canManage && data && (
        <>
          <SaveScopeRadio scope={scope} onChange={setScope} />
          <div className="flex justify-end">
            <Button variant="primary" size="sm" disabled={!dirty || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
              保存
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

const PRINT_DISPLAY_MODE_OPTIONS: { value: PrintDisplayMode; label: string }[] = [
  { value: 'time', label: '常に時刻を表示' },
  { value: 'pattern_name', label: '常にシフトパターン名を表示' },
];

/** 使い勝手フィードバック (2026-09-04、D-U13): シフト印刷 (`/shift/print`) の表示形式切替パネル。
 *  PreferenceInputModePanel と同型 (scalar 設定 + SaveScopeRadio、node_scalar_settings に相乗り)。 */
function PrintDisplayModePanel({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<ResolvedPrintDisplayMode>({
    queryKey: ['shift-print-display-mode', nodeId],
    queryFn: () => apiFetch(`/inheritable-settings/shift_print_display_mode/resolve?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });
  const initial = useRef<{ scope: SaveScope; value: PrintDisplayMode } | null>(null);
  const [scope, setScope] = useState<SaveScope>(SAVE_SCOPE.node);
  const [value, setValue] = useState<PrintDisplayMode>('time');
  useEffect(() => {
    if (!data) return;
    const seeded = {
      scope: (data.source_kind === 'tenant_default' || data.source_kind === 'none' ? SAVE_SCOPE.tenant : SAVE_SCOPE.node) as SaveScope,
      value: data.value ?? 'time',
    };
    initial.current = seeded;
    setScope(seeded.scope);
    setValue(seeded.value);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => apiFetch('/settings/shift-print/display-mode', {
      method: 'PUT',
      body: JSON.stringify({ node_id: scope === SAVE_SCOPE.tenant ? null : nodeId, value }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shift-print-display-mode'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const dirty = !!data && (!initial.current || value !== initial.current.value || scope !== initial.current.scope);

  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-text-body">シフト印刷の表示形式</h3>
        {data && <InheritanceSourceBadge sourceKind={data.source_kind} sourceNodeLabel={data.source_node_label} />}
      </div>
      {isLoading || !data ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : (
        <>
          <Select value={value} onValueChange={(v) => setValue(v as PrintDisplayMode)} disabled={!canManage}>
            <SelectTrigger className="w-full sm:w-80 min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRINT_DISPLAY_MODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-text-sub">
            掲示用のシフト印刷 (シフト運用 &gt; シフト印刷) の各枠に、シフトパターン名を表示するか時刻を表示するかを切り替えます。パターンが設定されていない割当は時刻表示にフォールバックします。
          </p>
        </>
      )}
      {canManage && data && (
        <>
          <SaveScopeRadio scope={scope} onChange={setScope} />
          <div className="flex justify-end">
            <Button variant="primary" size="sm" disabled={!dirty || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
              保存
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ⚠️ TD-22: このタブは守りが異なる2種類のエンドポイントを持つ。
//   canManagePatterns = POST/PUT/DELETE /settings/shift-patterns
//                       (requireTenantWideManage、パターン自体はテナント全体で共有されるため)
//   canManage         = PUT/DELETE /settings/shift-patterns/:id/times・希望入力モード
//                       (requireNodeManageScope、ノード単位判定なので own_store でも自店は可)
function PatternsTab({ nodeId, canManage, canManagePatterns }: { nodeId: string; canManage: boolean; canManagePatterns: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<{ pattern_id: string | null; code: string; label: string; color: string; sort_order: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Pattern | null>(null);
  const [editingTimeFor, setEditingTimeFor] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ patterns: Pattern[] }>({ queryKey: ['shift-patterns'], queryFn: () => apiFetch('/settings/shift-patterns') });
  const patterns = data?.patterns ?? [];

  const saveMut = useMutation({
    mutationFn: (d: NonNullable<typeof draft>) => {
      const payload = { code: d.code.trim(), label: d.label.trim(), color: d.color.trim() || null, sort_order: Number(d.sort_order) || 0 };
      return d.pattern_id
        ? apiFetch(`/settings/shift-patterns/${d.pattern_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/settings/shift-patterns', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shift-patterns'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/settings/shift-patterns/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shift-patterns'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-text-sub">早番・遅番等のプリセットを登録します。時刻はノードごとに上書きできます。</p>
        {canManagePatterns && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />}
            onClick={() => setDraft({ pattern_id: null, code: '', label: '', color: '', sort_order: String(patterns.length) })}>
            パターンを追加
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : patterns.length === 0 ? (
        <EmptyState message="シフトパターンがまだありません" description={canManagePatterns ? '「パターンを追加」から登録してください。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {patterns.map((p) => (
            <li key={p.pattern_id} className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex items-center gap-2">
                  {p.color && <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: p.color }} aria-hidden />}
                  <span className="text-sm font-semibold text-text-body break-words">{p.label}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{p.code}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setEditingTimeFor(p.pattern_id)} disabled={!nodeId}>{canManage ? '時刻を設定' : '時刻を確認'}</Button>
                  {canManagePatterns && (
                    <>
                      <button type="button" aria-label="編集" onClick={() => setDraft({ pattern_id: p.pattern_id, code: p.code, label: p.label, color: p.color ?? '', sort_order: String(p.sort_order) })}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                      <button type="button" aria-label="削除" onClick={() => setDeleteTarget(p)}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </div>
              {nodeId && editingTimeFor === p.pattern_id && (
                <PatternTimeEditor patternId={p.pattern_id} nodeId={nodeId} canManage={canManage} onClose={() => setEditingTimeFor(null)} />
              )}
            </li>
          ))}
        </ul>
      )}
      {!nodeId && <p className="text-xs text-text-sub">時刻を確認・設定するには上部で対象ノードを選んでください。</p>}

      {nodeId && <PreferenceInputModePanel nodeId={nodeId} canManage={canManage} />}
      {nodeId && <PrintDisplayModePanel nodeId={nodeId} canManage={canManage} />}

      <Modal open={!!draft} title={draft?.pattern_id ? 'パターンを編集' : 'パターンを追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50} className={inputCls} placeholder="例: early" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 早番" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">色 (任意)</label>
              <input value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })} maxLength={20} className={inputCls} placeholder="#FE6A03" />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="パターンを削除" message={`「${deleteTarget?.label}」を削除します。このパターンに紐づく時刻上書きも同時に削除されます。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.pattern_id)} />
    </div>
  );
}

function DayEditor({ day, nodeId, canManage, onClose }: { day: ResolvedDay; nodeId: string; canManage: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [scope, setScope] = useState<SaveScope>(day.source_kind === 'self' ? SAVE_SCOPE.node : SAVE_SCOPE.tenant);
  const [isClosed, setIsClosed] = useState(day.value?.is_closed ?? false);
  const [value, setValue] = useState<TimeRangeInputValue>({
    startTime: day.value?.open_time ?? '10:00', endTime: day.value?.close_time ?? '22:00',
  });

  const saveMut = useMutation({
    mutationFn: () => apiFetch('/settings/business-hours', {
      method: 'PUT',
      body: JSON.stringify({
        node_id: scope === SAVE_SCOPE.tenant ? null : nodeId,
        day_of_week: day.day_of_week,
        is_closed: isClosed,
        open_time: isClosed ? null : value.startTime,
        close_time: isClosed ? null : value.endTime,
        crosses_midnight: !isClosed && computesCrossesMidnight(value),
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['business-hours'] }); toast.success('保存しました'); onClose(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: () => apiFetch(`/settings/business-hours?node_id=${encodeURIComponent(nodeId)}&day_of_week=${day.day_of_week}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['business-hours'] }); toast.success('このノードの上書きを削除しました'); onClose(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const canSave = isClosed || (!!value.startTime && !!value.endTime);

  return (
    <Modal open title={`${DOW_LABELS[day.day_of_week]}曜日の営業時間`} onClose={onClose} size="sm"
      footer={(
        <>
          {canManage && day.source_kind === 'self' && <Button variant="ghost" onClick={() => deleteMut.mutate()} loading={deleteMut.isPending}>この上書きを削除</Button>}
          <Button variant="secondary" onClick={onClose}>{canManage ? 'キャンセル' : '閉じる'}</Button>
          {canManage && (
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
          )}
        </>
      )}>
      <div className="space-y-4">
        <InheritanceSourceBadge sourceKind={day.source_kind} sourceNodeLabel={day.source_node_label} />
        <label className="flex items-center gap-2 text-sm min-h-[44px]">
          <input type="checkbox" className="w-5 h-5" checked={isClosed} onChange={(e) => setIsClosed(e.target.checked)} disabled={!canManage} />
          休業日
        </label>
        {!isClosed && <TimeRangeInput value={value} onChange={setValue} disabled={!canManage} />}
        {canManage && <SaveScopeRadio scope={scope} onChange={setScope} />}
      </div>
    </Modal>
  );
}

type Exception = {
  exception_id: string; node_id: string; target_date: string;
  open_time: string | null; close_time: string | null; is_closed: boolean; reason: string | null;
};

/** 例外日の追加・編集を共通化したモーダル (v0.4 追加7要件③)。既存の「例外日を追加」ボタンと
 *  年間カレンダーの日付クリックの両方から使う。`existing` を渡すと編集モード (日付は固定表示、
 *  POST は target_date に対する delete→insert 全置換のため、日付欄を編集可能にしたまま送ると
 *  「元の日付が残ったまま新しい日付が作られる」事故になるため編集時は日付を変更不可にする)。 */
function ExceptionFormModal({
  nodeId, canManage, initialDate, existing, onClose,
}: { nodeId: string; canManage: boolean; initialDate: string; existing: Exception | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [targetDate, setTargetDate] = useState(initialDate);
  const [isClosed, setIsClosed] = useState(existing?.is_closed ?? true);
  const [value, setValue] = useState<TimeRangeInputValue>({
    startTime: existing?.open_time ?? '10:00', endTime: existing?.close_time ?? '22:00',
  });
  const [reason, setReason] = useState(existing?.reason ?? '');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['business-hours-exceptions', nodeId] });
  const saveMut = useMutation({
    mutationFn: () => apiFetch('/settings/business-hours/exceptions', {
      method: 'POST',
      body: JSON.stringify({
        node_id: nodeId, target_date: targetDate, is_closed: isClosed,
        open_time: isClosed ? null : value.startTime, close_time: isClosed ? null : value.endTime,
        crosses_midnight: !isClosed && computesCrossesMidnight(value),
        reason: reason.trim() || null,
      }),
    }),
    onSuccess: () => { invalidate(); toast.success(existing ? '更新しました' : '例外日を登録しました'); onClose(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: () => apiFetch(`/settings/business-hours/exceptions?node_id=${encodeURIComponent(nodeId)}&target_date=${targetDate}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); toast.success('削除しました'); onClose(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const canSave = isClosed || (!!value.startTime && !!value.endTime);

  return (
    <Modal open title={existing ? (canManage ? '例外日を編集' : '例外日を確認') : '例外日を追加'} onClose={onClose} size="sm"
      footer={(
        <>
          {canManage && existing && <Button variant="ghost" onClick={() => deleteMut.mutate()} loading={deleteMut.isPending}>削除</Button>}
          <Button variant="secondary" onClick={onClose}>{canManage ? 'キャンセル' : '閉じる'}</Button>
          {canManage && (
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
          )}
        </>
      )}>
      <div className="space-y-3">
        {existing ? (
          <p className="text-sm text-text-body">日付: {targetDate}</p>
        ) : (
          <div>
            <label className="block text-sm text-text-sub mb-1">日付</label>
            <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} className={inputCls} disabled={!canManage} />
          </div>
        )}
        <label className="flex items-center gap-2 text-sm min-h-[44px]">
          <input type="checkbox" className="w-5 h-5" checked={isClosed} onChange={(e) => setIsClosed(e.target.checked)} disabled={!canManage} />
          休業
        </label>
        {!isClosed && <TimeRangeInput value={value} onChange={setValue} disabled={!canManage} />}
        <div>
          <label className="block text-sm text-text-sub mb-1">理由 (任意)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} className={inputCls} placeholder="例: 年末年始休業" disabled={!canManage} />
        </div>
      </div>
    </Modal>
  );
}

function ExceptionsPanel({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  const [formDate, setFormDate] = useState<string | null>(null);
  const [editing, setEditing] = useState<Exception | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Exception | null>(null);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<{ exceptions: Exception[] }>({
    queryKey: ['business-hours-exceptions', nodeId],
    queryFn: () => apiFetch(`/settings/business-hours/exceptions?node_id=${encodeURIComponent(nodeId)}`),
  });
  const exceptions = data?.exceptions ?? [];

  const deleteMut = useMutation({
    mutationFn: (ex: Exception) => apiFetch(`/settings/business-hours/exceptions?node_id=${encodeURIComponent(nodeId)}&target_date=${ex.target_date}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['business-hours-exceptions', nodeId] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  return (
    <div className="space-y-2 pt-2 border-t border-surface-border">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-text-body">例外日 (祝日・年末年始等)</h3>
        {canManage && (
          <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />}
            onClick={() => setFormDate(new Date().toISOString().slice(0, 10))}>例外日を追加</Button>
        )}
      </div>
      {isLoading ? (
        <div className="text-xs text-text-sub">読み込み中…</div>
      ) : exceptions.length === 0 ? (
        <p className="text-xs text-text-sub">例外日は登録されていません。</p>
      ) : (
        <ul className="space-y-1">
          {exceptions.map((ex) => (
            <li key={ex.exception_id} className="flex items-center justify-between gap-2 text-xs border border-surface-line rounded p-2">
              <span>
                {ex.target_date} — {ex.is_closed ? '休業' : `${ex.open_time}〜${ex.close_time}`}
                {ex.reason && ` (${ex.reason})`}
              </span>
              <span className="flex items-center gap-1 shrink-0">
                <button type="button" aria-label="編集" onClick={() => setEditing(ex)}
                  className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                {canManage && (
                  <button type="button" aria-label="削除" onClick={() => setDeleteTarget(ex)}
                    className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {formDate && <ExceptionFormModal nodeId={nodeId} canManage={canManage} initialDate={formDate} existing={null} onClose={() => setFormDate(null)} />}
      {editing && <ExceptionFormModal nodeId={nodeId} canManage={canManage} initialDate={editing.target_date} existing={editing} onClose={() => setEditing(null)} />}

      <ConfirmDialog open={!!deleteTarget} title="例外日を削除" message={`${deleteTarget?.target_date} の例外設定を削除します。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget)} />
    </div>
  );
}

// =============================================================================
// 年間カレンダー (v0.4 追加7要件③、D-234 §6-3(b)(c))
// 定休日 (business_hours.is_closed) / 例外日 (business_hours_exceptions) / 祝日 (tenant_holidays) を
// 重ねて表示する。曜日文字の色 (calendar.saturday/sunday、要件①②) とは別軸の「営業状態」の色分け。
// =============================================================================

type DayStatus = 'open' | 'closed_weekly' | 'closed_exception' | 'short_exception';
const STATUS_LABELS: Record<DayStatus, string> = {
  open: '通常営業', closed_weekly: '定休日', closed_exception: '休業（例外）', short_exception: '時短（例外）',
};
const STATUS_MARKS: Record<DayStatus, string> = { open: '', closed_weekly: '定', closed_exception: '休', short_exception: '短' };
const STATUS_BG: Record<DayStatus, string> = {
  open: 'bg-surface-card', closed_weekly: 'bg-surface-sunken',
  closed_exception: 'bg-danger-bg', short_exception: 'bg-alert-bg',
};
const MONTH_LABELS = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
const pad2 = (n: number): string => String(n).padStart(2, '0');

function CalendarLegend() {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-sub">
      {(['open', 'closed_weekly', 'closed_exception', 'short_exception'] as DayStatus[]).map((s) => (
        <li key={s} className="flex items-center gap-1.5">
          <span className={`w-3 h-3 rounded-sm border border-surface-line shrink-0 ${STATUS_BG[s]}`} aria-hidden="true" />
          {STATUS_LABELS[s]}
        </li>
      ))}
    </ul>
  );
}

function resolveEffectiveDayStatus(
  date: string, hoursDays: ResolvedDay[] | undefined, exceptionsByDate: Map<string, Exception>,
): DayStatus {
  const exception = exceptionsByDate.get(date);
  if (exception) return exception.is_closed ? 'closed_exception' : 'short_exception';
  const day = hoursDays?.find((d) => d.day_of_week === dayOfWeekOf(date));
  return day?.value?.is_closed ? 'closed_weekly' : 'open';
}

function MonthGrid({
  year, month, hoursDays, exceptionsByDate, holidays, onDayClick,
}: {
  year: number; month: number; hoursDays: ResolvedDay[] | undefined;
  exceptionsByDate: Map<string, Exception>; holidays: ReadonlyMap<string, string>;
  onDayClick: (date: string) => void;
}) {
  const monthStr = `${year}-${pad2(month + 1)}`;
  const numDays = new Date(year, month + 1, 0).getDate();
  const firstDow = dayOfWeekOf(`${monthStr}-01`);
  const cells: (string | null)[] = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: numDays }, (_, i) => `${monthStr}-${pad2(i + 1)}`),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <div id={`ym-${year}-${pad2(month + 1)}`} className="border border-surface-border rounded-lg bg-white p-2 overflow-x-auto min-w-0 max-w-full">
      <p className="text-xs font-semibold text-text-body mb-1 text-center">{MONTH_LABELS[month]}</p>
      <table className="border-collapse">
        <thead>
          <tr>
            {DOW_LABELS.map((l, i) => (
              <th key={l} scope="col"
                className={`relative w-11 h-6 text-[10px] font-semibold ${i === 0 ? 'text-calendar-sunday' : i === 6 ? 'text-calendar-saturday' : 'text-text-sub'}`}>
                {l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((date, di) => {
                if (!date) return <td key={di} className="w-11 h-11" />;
                const status = resolveEffectiveDayStatus(date, hoursDays, exceptionsByDate);
                const holidayName = holidayNameOf(date, holidays);
                const dayNum = Number(date.slice(8, 10));
                return (
                  <td key={date} className="p-0">
                    <button type="button" onClick={() => onDayClick(date)}
                      className={`w-11 h-11 flex flex-col items-center justify-center gap-0 text-[11px] border border-surface-line focus-ring ${STATUS_BG[status]} hover:brightness-95`}
                      title={holidayName ?? undefined}
                      aria-label={`${date}${holidayName ? `（${holidayName}）` : ''}・${STATUS_LABELS[status]}`}>
                      <span className={dayTextClass(resolveDayKind(date, holidays))}>
                        {dayNum}
                        {holidayName && <span aria-hidden="true">・</span>}
                      </span>
                      {status !== 'open' && <span className="text-[9px] leading-none text-text-body" aria-hidden="true">{STATUS_MARKS[status]}</span>}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 期間指定 / 祝日リストの両方から使う一括登録モーダル本体。日付配列は呼び出し側が確定済みで渡す。 */
function BulkExceptionForm({
  nodeId, dates, defaultReason, onClose, extraNote, selectable, holidayNames,
}: {
  nodeId: string; dates: string[]; defaultReason: string; onClose: () => void; extraNote?: string;
  /** true のとき各日付をチェックボックスで選択解除できる (祝日一括登録モード) */
  selectable?: boolean; holidayNames?: ReadonlyMap<string, string>;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set(dates));
  const [isClosed, setIsClosed] = useState(true);
  const [value, setValue] = useState<TimeRangeInputValue>({ startTime: '10:00', endTime: '22:00' });
  const [reason, setReason] = useState(defaultReason);
  const [overwrite, setOverwrite] = useState(false);

  const effectiveDates = useMemo(() => dates.filter((d) => selected.has(d)), [dates, selected]);
  const tooMany = effectiveDates.length > 366;

  const saveMut = useMutation({
    mutationFn: () => apiFetch<{ registered: string[]; skipped_existing: string[] }>('/settings/business-hours/exceptions/bulk', {
      method: 'POST',
      body: JSON.stringify({
        node_id: nodeId, dates: effectiveDates, is_closed: isClosed,
        open_time: isClosed ? null : value.startTime, close_time: isClosed ? null : value.endTime,
        crosses_midnight: !isClosed && computesCrossesMidnight(value),
        reason: reason.trim() || null, overwrite_existing: overwrite,
      }),
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['business-hours-exceptions', nodeId] });
      const skippedNote = res.skipped_existing.length > 0 ? `（${res.skipped_existing.length}件は既存のためスキップ）` : '';
      toast.success(`${res.registered.length}件を登録しました${skippedNote}`);
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const canSave = effectiveDates.length > 0 && !tooMany && (isClosed || (!!value.startTime && !!value.endTime));

  return (
    <div className="space-y-3">
      {extraNote && <p className="text-xs text-text-sub">{extraNote}</p>}
      {tooMany && <p className="text-xs text-danger-text">一度に登録できるのは366日分までです。件数を減らしてください。</p>}
      {selectable && (
        <ul className="space-y-1 max-h-40 overflow-y-auto border border-surface-line rounded p-2">
          {dates.map((d) => (
            <li key={d}>
              <label className="flex items-center gap-2 text-sm min-h-[44px] cursor-pointer">
                <input type="checkbox" className="w-4 h-4 shrink-0" checked={selected.has(d)}
                  onChange={(e) => setSelected((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(d); else next.delete(d);
                    return next;
                  })} />
                <span>{d}{holidayNames?.get(d) ? `（${holidayNames.get(d)}）` : ''}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      <label className="flex items-center gap-2 text-sm min-h-[44px]">
        <input type="checkbox" className="w-5 h-5" checked={isClosed} onChange={(e) => setIsClosed(e.target.checked)} />
        休業にする
      </label>
      {!isClosed && <TimeRangeInput value={value} onChange={setValue} />}
      <div>
        <label className="block text-sm text-text-sub mb-1">理由 (任意)</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} className={inputCls} />
      </div>
      <label className="flex items-center gap-2 text-sm min-h-[44px]">
        <input type="checkbox" className="w-5 h-5" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
        既存の例外日を上書きする (未チェックの場合はスキップされます)
      </label>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={onClose}>キャンセル</Button>
        <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
          登録 ({effectiveDates.length}件)
        </Button>
      </div>
    </div>
  );
}

function BulkRangeModal({ nodeId, onClose }: { nodeId: string; onClose: () => void }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const dates = useMemo(() => (startDate && endDate && startDate <= endDate ? buildDateList(startDate, endDate) : []), [startDate, endDate]);

  return (
    <Modal open title="期間で一括登録" onClose={onClose} size="sm">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm text-text-sub mb-1">開始日</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">終了日</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} />
          </div>
        </div>
        {startDate > endDate && <p className="text-xs text-danger-text">終了日は開始日以降にしてください。</p>}
        <BulkExceptionForm key={`${startDate}:${endDate}`} nodeId={nodeId} dates={dates} defaultReason="" onClose={onClose} />
      </div>
    </Modal>
  );
}

function BulkFromHolidaysModal({
  nodeId, dates, holidayNames, onClose,
}: { nodeId: string; dates: string[]; holidayNames: ReadonlyMap<string, string>; onClose: () => void }) {
  return (
    <Modal open title="祝日をまとめて例外日に追加" onClose={onClose} size="sm">
      <BulkExceptionForm
        nodeId={nodeId} dates={dates} defaultReason="祝日" onClose={onClose} selectable holidayNames={holidayNames}
        extraNote="この店舗が祝日に休業するとは限らないため、対象と営業状態を確認してから登録してください。"
      />
    </Modal>
  );
}

type Holiday = { holiday_id: string; node_id: string | null; holiday_date: string; name: string; kind: 'public' | 'company' | 'other'; source: string };
const HOLIDAY_KIND_LABELS: Record<Holiday['kind'], string> = { public: '国民の祝日', company: '会社休日', other: 'その他' };

function HolidayManagementModal({
  nodeId, canManage, year, onClose,
}: { nodeId: string; canManage: boolean; year: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ holidays: Holiday[] }>({
    queryKey: ['tenant-holidays', nodeId, year],
    queryFn: () => apiFetch(`/tenant-holidays?year=${year}&node_id=${encodeURIComponent(nodeId)}`),
  });
  const holidays = data?.holidays ?? [];
  const [scope, setScope] = useState<SaveScope>(SAVE_SCOPE.tenant);
  const [draftDate, setDraftDate] = useState(`${year}-01-01`);
  const [draftName, setDraftName] = useState('');
  const [draftKind, setDraftKind] = useState<Holiday['kind']>('public');
  const [deleteTarget, setDeleteTarget] = useState<Holiday | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['tenant-holidays'] });
  const addMut = useMutation({
    mutationFn: () => apiFetch('/tenant-holidays', {
      method: 'POST',
      body: JSON.stringify({ node_id: scope === SAVE_SCOPE.tenant ? null : nodeId, holiday_date: draftDate, name: draftName.trim(), kind: draftKind }),
    }),
    onSuccess: () => { invalidate(); setDraftName(''); toast.success('登録しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const importMut = useMutation({
    mutationFn: () => apiFetch<{ imported: string[]; skipped_existing: number }>('/tenant-holidays/import', {
      method: 'POST',
      body: JSON.stringify({ node_id: scope === SAVE_SCOPE.tenant ? null : nodeId, year, country_code: 'JP' }),
    }),
    onSuccess: (res) => { invalidate(); toast.success(`${res.imported.length}件を取り込みました`); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '取込に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (h: Holiday) => apiFetch(`/tenant-holidays/${h.holiday_id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  return (
    <Modal open title={`祝日を管理 (${year}年)`} onClose={onClose} size="md"
      footer={<Button variant="secondary" onClick={onClose}>閉じる</Button>}>
      <div className="space-y-4">
        {canManage && (
          <>
            <Button variant="secondary" size="sm" loading={importMut.isPending} onClick={() => importMut.mutate()}>
              参照リストから{year}年分を取り込む
            </Button>
            <div className="border-t border-surface-border pt-3 space-y-2">
              <p className="text-xs font-semibold text-text-body">個別に登録</p>
              <SaveScopeRadio scope={scope} onChange={setScope} tenantLabel="テナント全体に登録"
                tenantHelperText="テナント全体に登録すると、店舗固有の祝日と合わせて全店舗の実効祝日として扱われます (上書きではなく追加、全店舗の管理権限が必要です)。" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} className={inputCls} />
                <input value={draftName} onChange={(e) => setDraftName(e.target.value)} maxLength={200} placeholder="祝日名" className={inputCls} />
                <Select value={draftKind} onValueChange={(v) => setDraftKind(v as Holiday['kind'])}>
                  <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(HOLIDAY_KIND_LABELS) as Holiday['kind'][]).map((k) => (
                      <SelectItem key={k} value={k}>{HOLIDAY_KIND_LABELS[k]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="secondary" size="sm" disabled={!draftName.trim() || addMut.isPending} loading={addMut.isPending} onClick={() => addMut.mutate()}>追加</Button>
            </div>
          </>
        )}
        <div className="border-t border-surface-border pt-3">
          {isLoading ? (
            <div className="text-text-sub text-sm">読み込み中…</div>
          ) : holidays.length === 0 ? (
            <p className="text-xs text-text-sub">この年の祝日は登録されていません。</p>
          ) : (
            <ul className="space-y-1">
              {holidays.map((h) => (
                <li key={h.holiday_id} className="flex items-center justify-between gap-2 text-xs border border-surface-line rounded p-2">
                  <span>
                    {h.holiday_date} — {h.name}
                    <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border">
                      {h.node_id ? '店舗固有' : 'テナント全体'}
                    </span>
                    {h.source === 'reference_import' && <span className="ml-1 text-[10px] text-text-sub">(取込)</span>}
                  </span>
                  {canManage && (
                    <button type="button" aria-label="削除" onClick={() => setDeleteTarget(h)}
                      className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <ConfirmDialog open={!!deleteTarget} title="祝日を削除" message={`「${deleteTarget?.name}」(${deleteTarget?.holiday_date}) を削除します。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget)} />
    </Modal>
  );
}

function YearCalendarPanel({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  const today = useMemo(() => new Date(), []);
  const currentYear = String(today.getFullYear());
  const currentMonthId = `ym-${currentYear}-${pad2(today.getMonth() + 1)}`;
  const [year, setYear] = useState(currentYear);
  const [dayModalDate, setDayModalDate] = useState<string | null>(null);
  const [bulkMode, setBulkMode] = useState<'range' | 'holidays' | null>(null);
  const [holidayPanelOpen, setHolidayPanelOpen] = useState(false);
  const pendingScrollToCurrentRef = useRef(false);

  useEffect(() => {
    if (!pendingScrollToCurrentRef.current) return;
    pendingScrollToCurrentRef.current = false;
    document.getElementById(currentMonthId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [year, currentMonthId]);

  const jumpToCurrentMonth = () => {
    if (year !== currentYear) {
      pendingScrollToCurrentRef.current = true;
      setYear(currentYear);
    } else {
      document.getElementById(currentMonthId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const { data: hoursData } = useQuery<{ days: ResolvedDay[] }>({
    queryKey: ['business-hours', nodeId],
    queryFn: () => apiFetch(`/settings/business-hours?node_id=${encodeURIComponent(nodeId)}`),
  });
  const { data: exceptionsData } = useQuery<{ exceptions: Exception[] }>({
    queryKey: ['business-hours-exceptions', nodeId],
    queryFn: () => apiFetch(`/settings/business-hours/exceptions?node_id=${encodeURIComponent(nodeId)}`),
  });
  const holidays = useHolidays(`${year}-01-01`, `${year}-12-31`, nodeId);

  const exceptionsByDate = useMemo(
    () => new Map((exceptionsData?.exceptions ?? []).map((e) => [e.target_date, e] as const)),
    [exceptionsData],
  );
  const unregisteredHolidayDates = useMemo(
    () => [...holidays.keys()].filter((d) => d.startsWith(year) && !exceptionsByDate.has(d)).sort(),
    [holidays, exceptionsByDate, year],
  );

  return (
    <div className="space-y-3 pt-2 border-t border-surface-border">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-text-body flex items-center gap-1.5"><CalendarDays size={15} aria-hidden />年間カレンダー</h3>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={jumpToCurrentMonth}>今月へ</Button>
          <button type="button" aria-label="前年" onClick={() => setYear((y) => String(Number(y) - 1))}
            className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold w-14 text-center tabular-nums">{year}年</span>
          <button type="button" aria-label="翌年" onClick={() => setYear((y) => String(Number(y) + 1))}
            className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><ChevronRight size={16} /></button>
        </div>
      </div>

      {canManage && (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => setBulkMode('range')}>期間で一括登録</Button>
          <Button variant="secondary" size="sm" disabled={unregisteredHolidayDates.length === 0} onClick={() => setBulkMode('holidays')}>
            祝日をまとめて例外日に追加{unregisteredHolidayDates.length > 0 ? ` (${unregisteredHolidayDates.length}件)` : ''}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setHolidayPanelOpen(true)}>祝日を管理</Button>
        </div>
      )}

      <CalendarLegend />

      <div className="flex flex-wrap gap-3">
        {Array.from({ length: 12 }, (_, m) => (
          <MonthGrid key={m} year={Number(year)} month={m} hoursDays={hoursData?.days} exceptionsByDate={exceptionsByDate}
            holidays={holidays} onDayClick={setDayModalDate} />
        ))}
      </div>

      {dayModalDate && (
        <ExceptionFormModal nodeId={nodeId} canManage={canManage} initialDate={dayModalDate}
          existing={exceptionsByDate.get(dayModalDate) ?? null} onClose={() => setDayModalDate(null)} />
      )}
      {bulkMode === 'range' && <BulkRangeModal nodeId={nodeId} onClose={() => setBulkMode(null)} />}
      {bulkMode === 'holidays' && (
        <BulkFromHolidaysModal nodeId={nodeId} dates={unregisteredHolidayDates} holidayNames={holidays} onClose={() => setBulkMode(null)} />
      )}
      {holidayPanelOpen && <HolidayManagementModal nodeId={nodeId} canManage={canManage} year={year} onClose={() => setHolidayPanelOpen(false)} />}
    </div>
  );
}

/** 追加7要件④ (D-240): 下書き自動生成の営業日フィルタ切替。既定 (未設定) は除外する=true。
 *  オフにすると、定休日にも仕込み・棚卸等の目的で自動生成が割当を作れるようになる (§6-3(a))。 */
function DraftSkipClosedDaysPanel({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<ResolvedSkipClosedDays>({
    queryKey: ['shift-draft-skip-closed-days', nodeId],
    queryFn: () => apiFetch(`/inheritable-settings/shift_draft_skip_closed_days/resolve?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });
  const initial = useRef<{ scope: SaveScope; value: boolean } | null>(null);
  const [scope, setScope] = useState<SaveScope>(SAVE_SCOPE.node);
  const [value, setValue] = useState(true);
  useEffect(() => {
    if (!data) return;
    // ⚠️ QA指摘: PatternTimeEditor (L108) と同じ安全側の判定にする (DayEditor の source_kind==='self'
    // 以外は全てtenant、という判定だと「上位ノードの上書きを継承しているだけ」の ancestor もtenant扱いに
    // なり、意図せずテナント全体の既定を書き換えてしまう)。ancestor は自ノードへの新規上書き (node) を既定にする。
    const seeded = {
      scope: (data.source_kind === 'tenant_default' || data.source_kind === 'none' ? SAVE_SCOPE.tenant : SAVE_SCOPE.node) as SaveScope,
      value: data.value !== false,
    };
    initial.current = seeded;
    setScope(seeded.scope);
    setValue(seeded.value);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => apiFetch('/settings/business-hours/skip-closed-days-in-draft', {
      method: 'PUT',
      body: JSON.stringify({ node_id: scope === SAVE_SCOPE.tenant ? null : nodeId, value }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shift-draft-skip-closed-days'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const dirty = !!data && (!initial.current || value !== initial.current.value || scope !== initial.current.scope);

  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-text-body">下書き自動生成の営業日フィルタ</h3>
        {data && <InheritanceSourceBadge sourceKind={data.source_kind} sourceNodeLabel={data.source_node_label} />}
      </div>
      {isLoading || !data ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : (
        <>
          <label className="flex items-start gap-2 text-sm min-h-[44px]">
            <input type="checkbox" className="w-5 h-5 mt-0.5 shrink-0" checked={value} disabled={!canManage}
              onChange={(e) => setValue(e.target.checked)} />
            定休日・休業日をシフト自動生成の対象から除外する
          </label>
          <p className="text-xs text-text-sub">
            オンの場合、休業日 (定休日・臨時休業) には自動生成で割当を作りません。オフにすると仕込み・棚卸などのために休業日にも自動生成の対象にできます。
            いずれの設定でも手動での割当は常に可能で、営業時間外の割当は違反 (警告) として記録されます。
          </p>
        </>
      )}
      {canManage && data && (
        <>
          <SaveScopeRadio scope={scope} onChange={setScope} />
          <div className="flex justify-end">
            <Button variant="primary" size="sm" disabled={!dirty || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
              保存
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** 追加7要件⑥ (D-236): 「制限しない (NULL)」か「N日」かを切り替えるパネル。
 *  DraftSkipClosedDaysPanel と同型だが、値が boolean ではなく nullable な非負整数のため
 *  「制限しない」チェックボックス + 数値入力の組で表現する。past-edit-grace-days/max-future-days の
 *  両方から使う (§8-55 対策: 差分は settingKey/endpoint/文言のみ)。 */
function NullableDaysPanel({
  nodeId, canManage, settingKey, endpoint, title, description,
}: {
  nodeId: string; canManage: boolean; settingKey: string; endpoint: string; title: string; description: string;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<ResolvedNullableDays>({
    queryKey: [settingKey, nodeId],
    queryFn: () => apiFetch(`/inheritable-settings/${settingKey}/resolve?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });
  const initial = useRef<{ scope: SaveScope; unlimited: boolean; value: number } | null>(null);
  const [scope, setScope] = useState<SaveScope>(SAVE_SCOPE.node);
  const [unlimited, setUnlimited] = useState(true);
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!data) return;
    // ⚠️ DraftSkipClosedDaysPanel/PatternTimeEditor と同じ安全側の判定 (ancestor は自ノードへの
    // 新規上書きを既定にする、上位ノードの上書きを継承しているだけの状態を誤ってテナント既定として保存しない)。
    const seeded = {
      scope: (data.source_kind === 'tenant_default' || data.source_kind === 'none' ? SAVE_SCOPE.tenant : SAVE_SCOPE.node) as SaveScope,
      unlimited: data.value === null,
      value: data.value ?? 0,
    };
    initial.current = seeded;
    setScope(seeded.scope);
    setUnlimited(seeded.unlimited);
    setValue(seeded.value);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => apiFetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify({ node_id: scope === SAVE_SCOPE.tenant ? null : nodeId, value: unlimited ? null : value }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [settingKey] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const dirty = !!data && (!initial.current
    || unlimited !== initial.current.unlimited
    || (!unlimited && value !== initial.current.value)
    || scope !== initial.current.scope);

  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-text-body">{title}</h3>
        {data && <InheritanceSourceBadge sourceKind={data.source_kind} sourceNodeLabel={data.source_node_label} />}
      </div>
      {isLoading || !data ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : (
        <>
          <label className="flex items-center gap-2 text-sm min-h-[44px]">
            <input type="checkbox" className="w-5 h-5 shrink-0" checked={unlimited} disabled={!canManage}
              onChange={(e) => setUnlimited(e.target.checked)} />
            制限しない
          </label>
          {!unlimited && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 min-h-[44px]">
                <input
                  type="number" min={0} step={1} value={value} disabled={!canManage}
                  onChange={(e) => setValue(Math.max(0, Number.parseInt(e.target.value, 10) || 0))}
                  className="w-24 min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring"
                />
                <span className="text-sm text-text-sub">日</span>
              </div>
              {/* design-expert HIGH-2 指摘: 「制限しない」を外した直後は既定値0のまま保存でき、
                  0日は「今日より前は即ロック」という運用上最も厳しい設定を意味するため無警告は危険。
                  値を明示的に確認させるインライン警告を出す (保存自体はブロックしない、意図的な0の
                  設定を妨げないため)。 */}
              {value === 0 && (
                <p className="text-xs text-alert-text">
                  0日を指定すると「今日より前の勤務日はすべて編集できない」という最も厳しい設定になります。意図した値か確認してください。
                </p>
              )}
            </div>
          )}
          <p className="text-xs text-text-sub">{description}</p>
        </>
      )}
      {canManage && data && (
        <>
          <SaveScopeRadio scope={scope} onChange={setScope} />
          <div className="flex justify-end">
            <Button variant="primary" size="sm" disabled={!dirty || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
              保存
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** v0.4 追加要件⑩ D-328 再設計 (D-337): 既定勤務パターンの生成見通し期間。TravelBufferPanel と
 *  同型 (「上書きしない場合は既定値」の非nullable整数設定、travel_buffer_minutes とは異なり
 *  既定値が0ではなく3ヶ月)。 */
function DefaultWorkPatternHorizonPanel({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const settingKey = 'default_work_pattern_horizon_months';
  const { data, isLoading } = useQuery<ResolvedNullableDays>({
    queryKey: [settingKey, nodeId],
    queryFn: () => apiFetch(`/inheritable-settings/${settingKey}/resolve?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });
  const initial = useRef<{ scope: SaveScope; overridden: boolean; value: number } | null>(null);
  const [scope, setScope] = useState<SaveScope>(SAVE_SCOPE.node);
  const [overridden, setOverridden] = useState(false);
  const [value, setValue] = useState(3);
  useEffect(() => {
    if (!data) return;
    const seeded = {
      scope: (data.source_kind === 'tenant_default' || data.source_kind === 'none' ? SAVE_SCOPE.tenant : SAVE_SCOPE.node) as SaveScope,
      overridden: data.source_kind === 'self',
      value: data.value ?? 3,
    };
    initial.current = seeded;
    setScope(seeded.scope);
    setOverridden(seeded.overridden);
    setValue(seeded.value);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => {
      const targetNodeId = scope === SAVE_SCOPE.tenant ? null : nodeId;
      const body = overridden ? { node_id: targetNodeId, value } : { node_id: targetNodeId, clear: true };
      return apiFetch('/settings/shift-periods/default-work-pattern-horizon-months', { method: 'PUT', body: JSON.stringify(body) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [settingKey] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const dirty = !!data && (!initial.current
    || overridden !== initial.current.overridden
    || (overridden && value !== initial.current.value)
    || scope !== initial.current.scope);

  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-text-body">既定勤務パターンの生成見通し期間</h3>
        {data && <InheritanceSourceBadge sourceKind={data.source_kind} sourceNodeLabel={data.source_node_label} />}
      </div>
      {isLoading || !data ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : (
        <>
          <label className="flex items-start gap-2 text-sm min-h-[44px]">
            <input type="checkbox" className="w-5 h-5 mt-0.5 shrink-0" checked={overridden} disabled={!canManage}
              onChange={(e) => setOverridden(e.target.checked)} />
            このノードで上書きする (未チェックの場合は上位ノード・テナント既定を継承)
          </label>
          {overridden && (
            <div className="flex items-center gap-2 min-h-[44px]">
              <input
                type="number" min={1} max={24} step={1} value={value} disabled={!canManage}
                onChange={(e) => setValue(Math.min(24, Math.max(1, Number.parseInt(e.target.value, 10) || 1)))}
                className="w-24 min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring"
              />
              <span className="text-sm text-text-sub">ヶ月</span>
            </div>
          )}
          <p className="text-xs text-text-sub">
            「既定勤務パターン」(/settings/default-work-patterns) が設定されている社員 (主に本社勤務者) について、
            今日からここで指定した月数先まで予定を自動生成し続けます。上書きしない場合は既定の3ヶ月として扱われます。
          </p>
        </>
      )}
      {canManage && data && (
        <>
          <SaveScopeRadio scope={scope} onChange={setScope} />
          <div className="flex justify-end">
            <Button variant="primary" size="sm" disabled={!dirty || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
              保存
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** 追加7要件⑥ (D-236): 期間の締め — 日付ベースの編集ロック・未来方向の制限。
 *  締めそのもの (closed 遷移) は /shift/periods 画面で行う (期間単位の操作のためノード別設定ではない)。
 *  ⚠️ design-expertレビュー Major指摘: v0.4 追加要件⑩の生成見通し期間は「編集ロック」ではなく
 *  別概念 (自動生成の対象期間) のため、見出し・導入文を共有せず区切り線+ 専用の説明文で明示的に分ける。 */
function PeriodLockTab({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  if (!nodeId) return <p className="text-sm text-text-sub">設定を確認・変更するには上部で対象ノードを選んでください。</p>;
  return (
    <div className="space-y-4">
      <p className="text-sm text-text-sub">
        期間そのものを締めて凍結する操作は「シフト期間」画面 (/shift/periods) から行います。ここでは締める前の
        うっかり編集を防ぐための補助設定を管理します。
      </p>
      <NullableDaysPanel
        nodeId={nodeId} canManage={canManage}
        settingKey="shift_past_edit_grace_days" endpoint="/settings/shift-periods/past-edit-grace-days"
        title="過去の勤務日の編集ロック"
        description="今日からここで指定した日数より前の勤務日は、割当の作成・変更・取消ができなくなります。期間そのものを締める (凍結する) わけではなく、権限があれば設定を変更していつでも編集を再開できます。"
      />
      <NullableDaysPanel
        nodeId={nodeId} canManage={canManage}
        settingKey="shift_period_max_future_days" endpoint="/settings/shift-periods/max-future-days"
        title="シフト期間の作成上限"
        description="新しいシフト期間の開始日を、今日からここで指定した日数より先には作成できなくなります。"
      />
      <div className="border-t border-surface-border pt-4 space-y-1">
        <p className="text-sm text-text-sub">
          以下は編集ロックとは別の設定です。店舗のシフトとは調整の要らない「既定勤務パターン」を持つ社員 (主に本社勤務者) の予定を、
          自動でどこまで先まで用意しておくかを管理します。
        </p>
      </div>
      <DefaultWorkPatternHorizonPanel nodeId={nodeId} canManage={canManage} />
    </div>
  );
}

function BusinessHoursTab({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  const [editingDay, setEditingDay] = useState<ResolvedDay | null>(null);
  const { data, isLoading } = useQuery<{ days: ResolvedDay[] }>({
    queryKey: ['business-hours', nodeId],
    queryFn: () => apiFetch(`/settings/business-hours?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });

  if (!nodeId) return <p className="text-sm text-text-sub">営業時間を確認・設定するには上部で対象ノードを選んでください。</p>;
  if (isLoading) return <div className="text-text-sub">読み込み中…</div>;

  return (
    <div className="space-y-4">
      <ul className="space-y-2">
        {(data?.days ?? []).map((day) => (
          <li key={day.day_of_week} className="flex items-center justify-between gap-2 flex-wrap border border-surface-border rounded-lg bg-white p-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-sm font-semibold text-text-body w-6 shrink-0">{DOW_LABELS[day.day_of_week]}</span>
              <span className="text-sm text-text-body">
                {day.value?.is_closed ? '休業' : day.value ? `${day.value.open_time}〜${day.value.close_time}` : '未設定'}
              </span>
              <InheritanceSourceBadge sourceKind={day.source_kind} sourceNodeLabel={day.source_node_label} />
            </div>
            <Button variant="ghost" size="sm" leftIcon={<Pencil size={14} />} onClick={() => setEditingDay(day)}>{canManage ? '編集' : '確認'}</Button>
          </li>
        ))}
      </ul>
      <DraftSkipClosedDaysPanel nodeId={nodeId} canManage={canManage} />
      <ExceptionsPanel nodeId={nodeId} canManage={canManage} />
      <YearCalendarPanel nodeId={nodeId} canManage={canManage} />
      {editingDay && <DayEditor day={editingDay} nodeId={nodeId} canManage={canManage} onClose={() => setEditingDay(null)} />}
    </div>
  );
}

/** 追加7要件⑦ (D-237): 移動時間の余裕 (バッファ)。grace_days/max_future_days と異なり「明示的な
 *  null」に業務上の意味を持たない (0分=バッファ無しで十分表現できる) ため、NullableDaysPanel の
 *  「制限しない」チェックボックスは流用せず「このノードで上書きする (継承する)」の専用パネルにする。 */
function TravelBufferPanel({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<ResolvedNullableDays>({
    queryKey: ['travel_buffer_minutes', nodeId],
    queryFn: () => apiFetch(`/inheritable-settings/travel_buffer_minutes/resolve?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });
  const initial = useRef<{ scope: SaveScope; overridden: boolean; value: number } | null>(null);
  const [scope, setScope] = useState<SaveScope>(SAVE_SCOPE.node);
  const [overridden, setOverridden] = useState(false);
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!data) return;
    const seeded = {
      scope: (data.source_kind === 'tenant_default' || data.source_kind === 'none' ? SAVE_SCOPE.tenant : SAVE_SCOPE.node) as SaveScope,
      overridden: data.source_kind === 'self',
      value: data.value ?? 0,
    };
    initial.current = seeded;
    setScope(seeded.scope);
    setOverridden(seeded.overridden);
    setValue(seeded.value);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => {
      const targetNodeId = scope === SAVE_SCOPE.tenant ? null : nodeId;
      const body = overridden ? { node_id: targetNodeId, value } : { node_id: targetNodeId, clear: true };
      return apiFetch('/settings/travel-buffer-minutes', { method: 'PUT', body: JSON.stringify(body) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['travel_buffer_minutes'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const dirty = !!data && (!initial.current
    || overridden !== initial.current.overridden
    || (overridden && value !== initial.current.value)
    || scope !== initial.current.scope);

  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-text-body">移動時間の余裕 (バッファ)</h3>
        {data && <InheritanceSourceBadge sourceKind={data.source_kind} sourceNodeLabel={data.source_node_label} />}
      </div>
      {isLoading || !data ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : (
        <>
          <label className="flex items-start gap-2 text-sm min-h-[44px]">
            <input type="checkbox" className="w-5 h-5 mt-0.5 shrink-0" checked={overridden} disabled={!canManage}
              onChange={(e) => setOverridden(e.target.checked)} />
            このノードで上書きする (未チェックの場合は上位ノード・テナント既定を継承)
          </label>
          {overridden && (
            <div className="flex items-center gap-2 min-h-[44px]">
              <input
                type="number" min={0} max={240} step={1} value={value} disabled={!canManage}
                onChange={(e) => setValue(Math.max(0, Number.parseInt(e.target.value, 10) || 0))}
                className="w-24 min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring"
              />
              <span className="text-sm text-text-sub">分</span>
            </div>
          )}
          <p className="text-xs text-text-sub">
            算出した移動所要時間 (実測値または緯度経度からの概算) にこの分数を上乗せしてから、
            前後のシフトの間隔と比較します。上書きしない場合は既定の0分として扱われます。
          </p>
        </>
      )}
      {canManage && data && (
        <>
          <SaveScopeRadio scope={scope} onChange={setScope} />
          <div className="flex justify-end">
            <Button variant="primary" size="sm" disabled={!dirty || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
              保存
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

type TravelTimeSource = 'manual' | 'haversine' | 'routing_api';
type TravelTimeEntry = { from_node_id: string; to_node_id: string; minutes: number; source: TravelTimeSource; computed_at: string };
const TRAVEL_SOURCE_LABEL: Record<TravelTimeSource, string> = {
  manual: '手入力 (実測値)', haversine: '概算 (緯度経度から自動計算)', routing_api: '経路API (未対応)',
};

/** 追加7要件⑦ (D-237): 手入力の実測値の管理 (フォールバック階層の最優先段)。
 *  緯度経度が両店舗に入力されていれば概算 (haversine) が自動で使われるため、実測値と大きく異なる
 *  場合や座標が無い店舗のためだけに登録すればよい。 */
function TravelTimeManualPanel({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const nameOf = (id: string) => flatNodes.find((f) => f.node.node_id === id)?.node.name ?? id;
  const otherNodeCandidates = useMemo(() => flatNodes.filter((f) => f.node.node_id !== nodeId), [flatNodes, nodeId]);

  const { data, isLoading } = useQuery<{ travel_times: TravelTimeEntry[] }>({
    queryKey: ['travel-times', nodeId],
    queryFn: () => apiFetch(`/settings/travel-times?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });

  const [draft, setDraft] = useState<{ direction: 'from' | 'to'; other_node_id: string; minutes: string } | null>(null);

  const saveMut = useMutation({
    mutationFn: (v: { from_node_id: string; to_node_id: string; minutes: number }) =>
      apiFetch('/settings/travel-times', { method: 'PUT', body: JSON.stringify(v) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['travel-times'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (v: { from_node_id: string; to_node_id: string }) =>
      apiFetch(`/settings/travel-times?from_node_id=${encodeURIComponent(v.from_node_id)}&to_node_id=${encodeURIComponent(v.to_node_id)}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['travel-times'] }); toast.success('削除しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  if (!nodeId) return <p className="text-sm text-text-sub">移動時間を確認・設定するには上部で対象ノードを選んでください。</p>;

  return (
    <div className="border border-surface-border rounded-lg bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-text-body">このノードに関わる移動時間</h3>
        {canManage && (
          <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft({ direction: 'from', other_node_id: '', minutes: '' })}>
            実測値を追加
          </Button>
        )}
      </div>
      {isLoading || !data ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : data.travel_times.length === 0 ? (
        <p className="text-sm text-text-sub">登録されている移動時間はありません (緯度経度が両店舗に入力されていれば概算が自動で使われます)。</p>
      ) : (
        <ul className="divide-y divide-surface-border">
          {data.travel_times.map((t) => (
            <li key={`${t.from_node_id}-${t.to_node_id}`} className="py-2 flex items-center justify-between gap-2 flex-wrap min-h-[44px]">
              <span className="text-sm text-text-body">
                {nameOf(t.from_node_id)} → {nameOf(t.to_node_id)}: {t.minutes}分
                <span className="text-xs text-text-sub ml-2">({TRAVEL_SOURCE_LABEL[t.source]})</span>
              </span>
              {canManage && t.source === 'manual' && (
                <Button variant="ghost" size="sm" disabled={deleteMut.isPending}
                  onClick={() => deleteMut.mutate({ from_node_id: t.from_node_id, to_node_id: t.to_node_id })}>
                  削除
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-text-sub">
        手入力の実測値は緯度経度からの概算より常に優先されます。片方向のみ登録した場合、逆方向は概算 (または未判定) のままです。
      </p>
      {draft && (
        <div className="border border-surface-border rounded p-3 space-y-2 bg-surface-sunken/40">
          <div className="flex rounded-full border border-surface-border overflow-hidden w-fit" role="group" aria-label="方向">
            {([['from', 'ここから'], ['to', 'ここへ']] as const).map(([v, label]) => (
              <button key={v} type="button" aria-pressed={draft.direction === v} onClick={() => setDraft({ ...draft, direction: v })}
                className={`px-3 min-h-[44px] sm:min-h-[32px] text-xs font-medium ${draft.direction === v ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                {label}
              </button>
            ))}
          </div>
          <Select value={draft.other_node_id || undefined} onValueChange={(v) => setDraft({ ...draft, other_node_id: v })}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="相手先ノードを選ぶ" /></SelectTrigger>
            <SelectContent>
              {otherNodeCandidates.map(({ node, depth }) => (
                <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 min-h-[44px]">
            <input
              type="number" min={0} max={1440} step={1} value={draft.minutes}
              onChange={(e) => setDraft({ ...draft, minutes: e.target.value })}
              className="w-24 min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring"
            />
            <span className="text-sm text-text-sub">分</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button
              variant="primary" size="sm"
              disabled={!draft.other_node_id || draft.minutes.trim() === '' || saveMut.isPending}
              loading={saveMut.isPending}
              onClick={() => {
                const minutes = Math.max(0, Number.parseInt(draft.minutes, 10) || 0);
                const fromNodeId = draft.direction === 'from' ? nodeId : draft.other_node_id;
                const toNodeId = draft.direction === 'from' ? draft.other_node_id : nodeId;
                saveMut.mutate({ from_node_id: fromNodeId, to_node_id: toNodeId, minutes });
              }}
            >
              保存
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** 追加7要件⑦ (D-237): 移動時間タブ — 余裕分の設定 + 手入力の実測値管理。
 *  座標の入力自体は組織階層 (/settings/hierarchy) の店舗編集モーダルで行う。 */
function TravelTimeTab({ nodeId, canManage }: { nodeId: string; canManage: boolean }) {
  if (!nodeId) return <p className="text-sm text-text-sub">設定を確認・変更するには上部で対象ノードを選んでください。</p>;
  return (
    <div className="space-y-4">
      <p className="text-sm text-text-sub">
        店舗の緯度経度は組織階層 (設定 &gt; 組織階層) の店舗編集画面で入力します。ここでは移動時間の判定に
        使う余裕分の設定と、手入力の実測値を管理します。
      </p>
      <TravelBufferPanel nodeId={nodeId} canManage={canManage} />
      <TravelTimeManualPanel nodeId={nodeId} canManage={canManage} />
    </div>
  );
}

// v0.4 追加要件⑪ 章② (D-354、§3-3): 時間帯区分マスタ。shift_patterns と異なりノード別上書きを
// 持たない (テナント全体1セット) ため NodeSelector を使わない。`tenant_config` ではなく `shift` で
// ゲートする (シフトの見え方に関する設定、Q-190 が未決の店長への tenant_config:manage 付与を待たない)。
function TimeBandsTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<{
    band_id: string | null; code: string; label: string;
    time: TimeRangeInputValue; color_token: string | null; icon_key: string | null; sort_order: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TimeBand | null>(null);

  const { data, isLoading } = useQuery<{ time_bands: TimeBand[] }>({
    queryKey: ['shift-time-bands'], queryFn: () => apiFetch('/settings/shift-time-bands'),
  });
  const bands = data?.time_bands ?? [];

  const saveMut = useMutation({
    mutationFn: (d: NonNullable<typeof draft>) => {
      const payload = {
        code: d.code.trim(), label: d.label.trim(),
        start_time: d.time.startTime, end_time: d.time.endTime,
        color_token: d.color_token, icon_key: d.icon_key,
        sort_order: Number(d.sort_order) || 0,
      };
      return d.band_id
        ? apiFetch(`/settings/shift-time-bands/${d.band_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/settings/shift-time-bands', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shift-time-bands'] }); qc.invalidateQueries({ queryKey: ['shift-time-bands-reference'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/settings/shift-time-bands/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift-time-bands'] });
      qc.invalidateQueries({ queryKey: ['shift-time-bands-reference'] });
      setDeleteTarget(null); toast.success('削除しました');
    },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '' && !!draft.time.startTime && !!draft.time.endTime;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-text-sub">
          編成ボードでの色分けに使う時間帯 (朝勤・夜勤等) を登録します。開始時刻が区分の範囲内に入る枠が自動的に色分けされます。
          {/* QA Major指摘反映: 「shift:manage」は own_store/node_scope でも真になるが、この設定は
              テナント全体で共有されるため保存には全店舗の管理権限が必要 (バックエンドの
              requireShiftTenantWideManage)。フロントは保有 scope を判定できないため
              (effective_permissions は feature+action の有無のみを持つ)、事前に条件を明示しておく。 */}
          {' '}店舗ごとの上書きは無く、保存には全店舗の管理権限が必要です。
        </p>
        {canManage && (
          <Button
            variant="secondary" size="sm" leftIcon={<Plus size={14} />}
            onClick={() => setDraft({
              band_id: null, code: '', label: '', time: { startTime: '09:00', endTime: '17:00' },
              color_token: null, icon_key: null, sort_order: String(bands.length),
            })}
          >
            時間帯区分を追加
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : bands.length === 0 ? (
        <EmptyState message="時間帯区分がまだありません" description={canManage ? '「時間帯区分を追加」から登録してください (未登録の場合は色分けされません)。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {bands.map((b) => {
            const Icon = b.icon_key ? TIME_BAND_ICON_COMPONENTS[b.icon_key] : null;
            const colorCls = b.color_token ? TIME_BAND_COLOR_CLASSES[b.color_token] : null;
            return (
              <li key={b.band_id} className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex items-center gap-2">
                    <span aria-hidden className={`w-3 h-3 rounded-sm shrink-0 ${colorCls ?? 'bg-surface-sunken border border-surface-border'}`} />
                    {Icon && <Icon size={14} aria-hidden className="text-text-sub shrink-0" />}
                    <span className="text-sm font-semibold text-text-body break-words">{b.label}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{b.code}</span>
                    <span className="text-xs text-text-sub tabular-nums whitespace-nowrap">{b.start_time}〜{b.end_time}</span>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button" aria-label="編集"
                        onClick={() => setDraft({
                          band_id: b.band_id, code: b.code, label: b.label,
                          time: { startTime: b.start_time, endTime: b.end_time },
                          color_token: b.color_token, icon_key: b.icon_key, sort_order: String(b.sort_order),
                        })}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"
                      ><Pencil size={14} /></button>
                      <button
                        type="button" aria-label="削除" onClick={() => setDeleteTarget(b)}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"
                      ><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.band_id ? '時間帯区分を編集' : '時間帯区分を追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50} className={inputCls} placeholder="例: morning" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 朝勤" />
            </div>
            <TimeRangeInput value={draft.time} onChange={(time) => setDraft({ ...draft, time })} legend="時間帯の範囲 (開始時刻がこの範囲内の枠が対象)" />
            <div>
              <label className="block text-sm text-text-sub mb-1">色 (任意)</label>
              <Select value={draft.color_token ?? '__none__'} onValueChange={(v) => setDraft({ ...draft, color_token: v === '__none__' ? null : v })}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">色なし</SelectItem>
                  {TIME_BAND_COLOR_TOKENS.map((tok) => (
                    <SelectItem key={tok} value={tok}>
                      <span className="inline-flex items-center gap-1.5">
                        <span aria-hidden className={`w-2.5 h-2.5 rounded-sm ${TIME_BAND_COLOR_CLASSES[tok]}`} />{TIME_BAND_COLOR_LABELS[tok]}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">アイコン (任意)</label>
              <Select value={draft.icon_key ?? '__none__'} onValueChange={(v) => setDraft({ ...draft, icon_key: v === '__none__' ? null : v })}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">アイコンなし</SelectItem>
                  {TIME_BAND_ICON_KEYS.map((key) => (
                    <SelectItem key={key} value={key}>{TIME_BAND_ICON_LABELS[key]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">表示順 (重なった場合は小さい方が優先)</label>
              <input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} className={inputCls} />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="時間帯区分を削除" message={`「${deleteTarget?.label}」を削除します。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.band_id)} />
    </div>
  );
}

export function ShiftPatternsSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const permsReady = usePermsReady();
  const canManage = hasPermission(user?.effective_permissions, 'tenant_config', 'manage');
  // ⚠️ TD-22: パターン自体の CRUD だけは requireTenantWideManage で全店舗スコープを要求する
  // (時刻・営業時間・移動時間はノード単位判定なので canManage のまま)。
  const canManagePatterns = hasTenantWidePermission(user?.effective_permissions, 'tenant_config', 'manage');
  const canManageTimeBands = hasTenantWidePermission(user?.effective_permissions, 'shift_periods', 'manage');
  const [nodeId, setNodeId] = useState('');
  const [tab, setTab] = useState<'patterns' | 'hours' | 'periods' | 'travel' | 'time-bands'>('patterns');

  // ⚠️ TD-22: タブごとにバックエンドの要求スコープが異なるため注記も出し分ける。
  // パターン自体の CRUD と時間帯区分は全店舗スコープ、時刻・営業時間・締め・移動時間は対象ノード単位。
  // design-and-codeレビュー指摘: permsReady追加でネスト三項演算子が読みにくくなったため、
  // 表示する注記文をここで一本化してから描画側は単純な `{bannerText && <p>...}` にする。
  const scopeNoteText: string | null = !permsReady
    ? null
    : tab === 'time-bands'
      ? (!canManageTimeBands ? '閲覧のみ可能です (時間帯区分の編集には全店舗の管理権限が必要です)。' : null)
      : !canManage
        ? '閲覧のみ可能です (編集には対象ノードの管理権限が必要です)。'
        : (tab === 'patterns' && !canManagePatterns
          ? '時刻・希望入力モードは編集できますが、パターン自体の追加・編集・削除には全店舗の管理権限が必要です。'
          : null);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">シフトパターン・営業時間</h2>
        <p className="text-sm text-text-sub mt-1">
          早番・遅番等のプリセットと、店舗の営業時間を管理します。時刻はノードごとに上書きでき、上書きしていない範囲は上位ノードやテナント全体の既定値を継承します。
        </p>
        {scopeNoteText && <p className="text-xs text-text-sub mt-1">{scopeNoteText}</p>}
      </div>

      {/* v0.4 追加要件⑪ 章② (D-354): 時間帯区分はテナント全体1セット (ノード別上書きを持たない) */}
      {tab !== 'time-bands' && <NodeSelector nodeId={nodeId} onChange={setNodeId} />}

      <div className="border-b border-surface-border overflow-x-auto">
        <nav className="flex gap-1 min-w-max" aria-label="タブ">
          {([['patterns', 'パターン'], ['hours', '営業時間'], ['periods', '期間の締め'], ['travel', '移動時間'], ['time-bands', '時間帯区分']] as const).map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)} aria-current={tab === key}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 min-h-[44px] transition ${
                tab === key ? 'border-brand-red text-brand-red font-semibold' : 'border-transparent text-text-sub hover:text-text-body hover:border-surface-border'
              }`}>
              {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'patterns' && <PatternsTab nodeId={nodeId} canManage={canManage} canManagePatterns={canManagePatterns} />}
      {tab === 'hours' && <BusinessHoursTab nodeId={nodeId} canManage={canManage} />}
      {tab === 'periods' && <PeriodLockTab nodeId={nodeId} canManage={canManage} />}
      {tab === 'travel' && <TravelTimeTab nodeId={nodeId} canManage={canManage} />}
      {tab === 'time-bands' && <TimeBandsTab canManage={canManageTimeBands} />}
    </div>
  );
}
